import { useEffect, useCallback, useRef } from 'react';
import { AppNode, Connection, NodeStatus, NodeType } from '../types';
import { unifiedStorage } from '../services/unifiedStorage';
import { useGroupStore } from '../stores/groupStore';
import { useCanvasStore } from '../stores/canvasStore';
import { useTaskStore } from '../stores/taskStore';
import { config } from '../config';

interface UseDataPersistenceParams {
    isLoaded: boolean;
    dataLoadSucceeded: boolean;
    isInEditor: boolean;
    currentProjectId: string | null;
    nodes: AppNode[];
    connections: Connection[];
    assetHistory: any[];
    workflows: any[];
    setNodes: (nodes: AppNode[]) => void;
    setConnections: (conns: Connection[]) => void;
    setAssetHistory: (assets: any[]) => void;
    setWorkflows: (wfs: any[]) => void;
    setDataLoadSucceeded: (v: boolean) => void;
    setIsLoaded: (v: boolean) => void;
    getThumbnail: () => string;
}

let persistedGroups: any[] = [];

export function getPersistedGroups(): any[] {
    return persistedGroups;
}

export function setPersistedGroups(groups: any[]) {
    persistedGroups = groups;
}

export function nodeHasResult(node: AppNode): boolean {
    const d = node.data;
    if (d.image || (d.images && d.images.length > 0)) return true;
    if (d.videoUri || (d.videoUris && d.videoUris.length > 0)) return true;
    if (d.audioUri) return true;
    if (d.panoramaOutputUrl) return true;
    if (d.analysis) return true;
    return false;
}

function fixAssetPaths(nodes: AppNode[]): AppNode[] {
    return nodes.map(node => {
        const d = node.data;
        let fixed = false;
        const nd = { ...d };
        if (typeof nd.image === 'string' && nd.image.includes('\\')) { nd.image = nd.image.replace(/\\/g, '/'); fixed = true; }
        if (Array.isArray(nd.images)) { const f = nd.images.map((img: string) => typeof img === 'string' && img.includes('\\') ? (fixed = true, img.replace(/\\/g, '/')) : img); if (fixed) nd.images = f; }
        if (typeof nd.videoUri === 'string' && nd.videoUri.includes('\\')) { nd.videoUri = nd.videoUri.replace(/\\/g, '/'); fixed = true; }
        if (Array.isArray(nd.videoUris)) { const f = nd.videoUris.map((vid: string) => typeof vid === 'string' && vid.includes('\\') ? (fixed = true, vid.replace(/\\/g, '/')) : vid); if (fixed) nd.videoUris = f; }
        if (typeof nd.audioUri === 'string' && nd.audioUri.includes('\\')) { nd.audioUri = nd.audioUri.replace(/\\/g, '/'); fixed = true; }
        if (typeof nd.panoramaOutputUrl === 'string' && nd.panoramaOutputUrl.includes('\\')) { nd.panoramaOutputUrl = nd.panoramaOutputUrl.replace(/\\/g, '/'); fixed = true; }
        return fixed ? { ...node, data: nd } : node;
    });
}

function reconcileNodeStatuses(nodes: AppNode[]): AppNode[] {
    const taskStore = useTaskStore.getState();
    const pendingTaskNodeIds = new Set<string>();
    const completedTaskNodeIds = new Set<string>();
    taskStore.pendingTasks.forEach((task) => {
        if (task.status === 'RUNNING') {
            pendingTaskNodeIds.add(task.nodeId);
        } else if (task.status === 'SUCCESS') {
            completedTaskNodeIds.add(task.nodeId);
        }
    });

    return nodes.map((node) => {
        if (node.status !== NodeStatus.WORKING) {
            if (node.data.activeFeature) {
                return { ...node, data: { ...node.data, activeFeature: undefined } };
            }
            return node;
        }

        if (node.type === NodeType.MEDIA_UPLOAD) {
            return { ...node, status: NodeStatus.IDLE, data: { ...node.data, activeFeature: undefined } };
        }

        if (nodeHasResult(node)) {
            return { ...node, status: NodeStatus.SUCCESS, data: { ...node.data, activeFeature: undefined } };
        }

        if (pendingTaskNodeIds.has(node.id)) {
            return node;
        }

        if (completedTaskNodeIds.has(node.id)) {
            return { ...node, status: NodeStatus.WORKING, data: { ...node.data, progress: '任务已完成，正在恢复结果...', activeFeature: undefined } };
        }

        return { ...node, status: NodeStatus.WORKING, data: { ...node.data, progress: '正在检查任务状态...', activeFeature: undefined, _needsTaskRecovery: true } };
    });
}

export function useDataPersistence({
    isLoaded,
    dataLoadSucceeded,
    isInEditor,
    currentProjectId,
    nodes,
    connections,
    assetHistory,
    workflows,
    setNodes,
    setConnections,
    setAssetHistory,
    setWorkflows,
    setDataLoadSucceeded,
    setIsLoaded,
    getThumbnail,
}: UseDataPersistenceParams) {
    const nodesRef = useRef(nodes);
    const connectionsRef = useRef(connections);
    const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isDraggingRef = useRef(false);

    useEffect(() => {
        nodesRef.current = nodes;
        connectionsRef.current = connections;
    }, [nodes, connections]);

    useEffect(() => {
        const handleDragStart = () => {
            isDraggingRef.current = true;
        };
        const handleDragStop = () => {
            // P2-2: 延迟设置 isDraggingRef 为 false，让保存逻辑使用更长的延迟
            // 这样拖拽停止后 UI 能先完成渲染，避免保存操作阻塞主线程
            setTimeout(() => {
                isDraggingRef.current = false;
            }, 100);
        };
        window.addEventListener('flow-drag-start', handleDragStart);
        window.addEventListener('flow-drag-stop', handleDragStop);
        return () => {
            window.removeEventListener('flow-drag-start', handleDragStart);
            window.removeEventListener('flow-drag-stop', handleDragStop);
        };
    }, []);

    const loadDataFromStorage = useCallback(async () => {
        try {
            if (currentProjectId && isInEditor) {
                const canvasData = await unifiedStorage.getCanvas(currentProjectId);
                if (canvasData && canvasData.nodes && canvasData.nodes.length > 0) {
                    const reconciledNodes = reconcileNodeStatuses(fixAssetPaths(canvasData.nodes));
                    setNodes(reconciledNodes);
                    useGroupStore.getState().rebuildGroupsFromNodesData(reconciledNodes);
                    useCanvasStore.getState().rebuildSpatialIndex();
                    if (canvasData.edges) setConnections(canvasData.edges);
                    if (canvasData.groups) persistedGroups = canvasData.groups;
                    setDataLoadSucceeded(true);
                    setIsLoaded(true);
                    return;
                }
            }

            const sAssets = await unifiedStorage.loadFromIndexedDB<any[]>('assets');
            if (sAssets) setAssetHistory(sAssets);
            const sWfs = await unifiedStorage.loadFromIndexedDB<any[]>('workflows');
            if (sWfs) setWorkflows(sWfs);

            if (isInEditor) {
                let sNodes = await unifiedStorage.loadFromIndexedDB<AppNode[]>('nodes');
                let sConns = await unifiedStorage.loadFromIndexedDB<Connection[]>('connections');
                let sGroups = await unifiedStorage.loadFromIndexedDB<any[]>('groups');
                if (!sNodes || sNodes.length === 0) {
                    const backup = await unifiedStorage.loadEmergencyBackup();
                    if (backup.nodes && Array.isArray(backup.nodes) && backup.nodes.length > 0) {
                        console.warn('IndexedDB nodes empty, restoring from emergency localStorage');
                        sNodes = backup.nodes;
                        if (backup.connections) sConns = backup.connections;
                        if (backup.groups) sGroups = backup.groups;
                    }
                }
                if (sNodes && sNodes.length > 0) {
                    const reconciledNodes = reconcileNodeStatuses(fixAssetPaths(sNodes));
                    setNodes(reconciledNodes);
                    useGroupStore.getState().rebuildGroupsFromNodesData(reconciledNodes);
                    useCanvasStore.getState().rebuildSpatialIndex();
                }
                if (sConns) setConnections(sConns);
                if (sGroups) persistedGroups = sGroups;
            }

            setDataLoadSucceeded(true);
        } catch (e) {
            console.error("Failed to load storage", e);
        } finally {
            setIsLoaded(true);
        }
    }, [currentProjectId, isInEditor]);

    useEffect(() => {
        const handleBeforeUnload = () => {
            const data = {
                nodes: nodesRef.current,
                connections: connectionsRef.current,
                groups: persistedGroups,
            };
            unifiedStorage.flush(currentProjectId, data);
            if (currentProjectId && data.nodes && data.nodes.length > 0) {
                try {
                    const xhr = new XMLHttpRequest();
                    xhr.open('PUT', `/api/projects/${currentProjectId}`, false);
                    xhr.setRequestHeader('Content-Type', 'application/json');
                    xhr.send(JSON.stringify({
                        workflow_data: { nodes: data.nodes, connections: data.connections, groups: data.groups },
                    }));
                } catch {}
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [currentProjectId]);

    useEffect(() => {
        if (!isLoaded) return;
        if (!dataLoadSucceeded && nodes.length === 0) return;

        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
        }

        const doSave = () => {
            unifiedStorage.saveToIndexedDB('assets', assetHistory);
            unifiedStorage.saveToIndexedDB('workflows', workflows);
            unifiedStorage.saveToIndexedDB('nodes', nodes);
            unifiedStorage.saveToIndexedDB('connections', connections);
            unifiedStorage.saveToIndexedDB('groups', persistedGroups);
            if (isInEditor && currentProjectId) {
                unifiedStorage.saveProjectCache(currentProjectId, { nodes, connections, groups: persistedGroups });
                unifiedStorage.saveToBackend(currentProjectId, { nodes, connections, groups: persistedGroups });
                unifiedStorage.saveCanvas(currentProjectId, {
                    nodes,
                    edges: connections,
                    groups: persistedGroups,
                    viewport: { x: 0, y: 0, zoom: 1 },
                });
            }
            saveTimerRef.current = null;
        };

        if (isDraggingRef.current) {
            saveTimerRef.current = setTimeout(doSave, 2000);
        } else {
            saveTimerRef.current = setTimeout(doSave, 500);
        }

        return () => {
            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
                saveTimerRef.current = null;
            }
        };
    }, [assetHistory, workflows, nodes, connections, isLoaded, dataLoadSucceeded, isInEditor, currentProjectId]);

    useEffect(() => {
        if (!isLoaded || !isInEditor || !currentProjectId) return;
        unifiedStorage.startAutoSave(
            currentProjectId,
            () => ({ nodes: nodesRef.current, connections: connectionsRef.current, groups: persistedGroups }),
            getThumbnail
        );
        return () => unifiedStorage.stopAutoSave();
    }, [isLoaded, isInEditor, currentProjectId, getThumbnail]);

    return { loadDataFromStorage };
}
